/**
 * smSplineStyle — user view-preference for the SHAPE of transition splines in
 * State Machine graphs (ENG-1061).
 *
 * The default `step` splines (getSmoothStepPath, 90° rounded corners) overlap
 * badly on dense state machines and it becomes impossible to tell which line
 * connects which states. This lets the user switch to:
 *   - `arched`   — curved bezier splines (getBezierPath)
 *   - `straight` — direct straight lines (getStraightPath)
 *
 * This is a per-user VIEW preference, not blueprint data: it changes only how
 * the graph is drawn, never what it means or what gets saved to the server. So
 * it lives in localStorage (like other editor view prefs), carries no schema
 * version, and is never written into a blueprint row. TransitionEdge.jsx reads
 * the chosen style off `edge.data.splineStyle` and picks the matching ReactFlow
 * path function.
 */
const KEY = 'aos-sm-spline-style'

// Arched (curved bezier) is the default: the old right-angle `step` splines
// overlapped into an unreadable tangle on dense machines, which is the whole
// reason ENG-1061 exists. A user's saved choice always wins over this.
export const DEFAULT_SPLINE_STYLE = 'arched'

// Ordered for the toolbar segmented control.
export const SPLINE_STYLES = [
  { value: 'step',     label: 'Step',     title: 'Right-angle stepped splines' },
  { value: 'arched',   label: 'Arched',   title: 'Curved bezier splines (default)' },
  { value: 'straight', label: 'Straight', title: 'Direct straight lines' },
]

const VALID = new Set(SPLINE_STYLES.map(s => s.value))

export function isValidSplineStyle(v) {
  return VALID.has(v)
}

/** Current SM spline style; falls back to the default for missing/invalid values. */
export function getSmSplineStyle() {
  try {
    const v = globalThis.localStorage?.getItem(KEY)
    return isValidSplineStyle(v) ? v : DEFAULT_SPLINE_STYLE
  } catch {
    return DEFAULT_SPLINE_STYLE
  }
}

/**
 * Persist the SM spline style. Ignores invalid values (returns the effective
 * style either way) so a bad caller can never wedge the editor into a broken
 * shape.
 */
export function setSmSplineStyle(v) {
  if (!isValidSplineStyle(v)) return getSmSplineStyle()
  try {
    globalThis.localStorage?.setItem(KEY, v)
  } catch {
    // Non-fatal: a private-mode / disabled localStorage just means the choice
    // doesn't survive a reload — the in-session React state still applies it.
  }
  return v
}
